import { useEffect } from "react";
import { ProgressBar } from "@cruk/cruk-react-components";

import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";

import { ProgressListButton } from "@fwa/src/components/ProgressListButton";
import { Share } from "@fwa/src/components/Share";

import sharedStyles from "@fwa/src/components/styles.module.css";

import { type ProgressItemType, type PageType } from "@fwa/src/types";

export const completionStats = (progressItems: ProgressItemType[]) => {
  const doneCount: number =
    progressItems.reduce(
      (accumulator, progressItem): number =>
        progressItem.isDone ? accumulator + 1 : accumulator,
      0,
    ) || 0;
  const percentageComplete = Math.floor(
    (doneCount / progressItems.length) * 100,
  ); // floor or round here? difference is basically 66% vs 67% for 2/3
  const complete = percentageComplete === 100;

  return { doneCount, percentageComplete, complete };
};

let percentageCompleteOnFirstLoad = 0;

export const ProgressList = ({
  progressItems,
  page,
}: {
  progressItems: ProgressItemType[];
  page: PageType;
}) => {
  const { trackEventOptimizely } = useOptimizelyContext();
  const { percentageComplete } = completionStats(progressItems);

  useEffect(() => {
    percentageCompleteOnFirstLoad = percentageComplete;
    // we only want this to happen on first load so empty deps array
  }, []);

  useEffect(() => {
    if (!trackEventOptimizely) return;
    if (percentageComplete !== percentageCompleteOnFirstLoad) {
      trackEventOptimizely({
        eventKey: "of_progress_bar_completion",
        eventTags: { percentageComplete },
      });
    }
  }, [percentageComplete, trackEventOptimizely]);

  return (
    <>
      <ul className={sharedStyles["list"]}>
        {progressItems.map((progressItem: ProgressItemType) => (
          <li
            style={{ marginBottom: "var(--spacing-xs, 1rem)" }}
            key={`progressItem_${progressItem.fieldName || ""}`}
          >
            <div className={sharedStyles["progress-row"]}>
              {progressItem.fieldName === "share" ? (
                <Share
                  page={page}
                  feedItem={null}
                  isOwner
                  popoverPosition="top"
                  ctaLocation="progress-list"
                >
                  <ProgressListButton
                    progressItem={progressItem}
                  ></ProgressListButton>
                </Share>
              ) : (
                <ProgressListButton
                  progressItem={progressItem}
                ></ProgressListButton>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className={sharedStyles["block-center-mobile"]}>
        <ProgressBar
          isCircular
          percentage={percentageComplete}
          circleContents={`${percentageComplete}%`}
        />
      </div>
    </>
  );
};

export default ProgressList;
